import styles from "./ProductDeliveryReturns.module.scss";
import { SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { ReturnsExclusions } from "@/components/ProductAccordion/components/ProductDeliveryOptionsAccordion/ReturnsExclusions/ReturnsExclusions.tsx";
import { checkTimedContentIsActive } from "@/components/ProductAccordion/components/ProductDeliveryOptionsAccordion/util/checkTimedContentIsActive.ts";

export type Props = {
  browsePageHierarchy: Array<{ categoryId: string }>;
  sellingProductType: string;
  showOutletReturnsMessage?: boolean;
  isBladedArticle?: boolean;
  isM2M?: boolean;
  returnsStartDate?: string;
  returnsEndDate?: string;
  showChristmasDeliveryMessaging: boolean;
  isMeteredProduct?: boolean;
};

export const ProductDeliveryReturns = ({
  browsePageHierarchy,
  sellingProductType = "",
  showOutletReturnsMessage = false,
  isBladedArticle = false,
  isM2M = false,
  returnsStartDate,
  returnsEndDate,
  isMeteredProduct = false,
  showChristmasDeliveryMessaging,
}: Props) => {
  const showTimedFrameCopy = checkTimedContentIsActive(
    returnsStartDate,
    returnsEndDate,
  );

  const ReturnsRestrictionsMessage = () => {
    if (isBladedArticle)
      return (
        <DEPRECATED_Message
          type="information"
          title="Returns restrictions apply to this item"
          body="You can return this item to a John Lewis shop, or by Royal Mail. You cannot return bladed items to Waitrose stores, or any other return locations."
          buttonText="Find out how to buy and return bladed items"
          buttonProps={{
            href: "https://www.johnlewis.com/customer-services/shopping-with-us/buying-bladed-items",
            rel: "noopener noreferrer",
            tag: "a",
          }}
        />
      );

    if (isMeteredProduct || isM2M)
      return (
        <DEPRECATED_Message
          type="information"
          title="Returns restrictions apply to products that are made to order or made to measure"
          body={
            "Unless faulty, you cannot return, cancel or exchange products that are made to order or made to measure. This does not affect your statutory rights."
          }
          buttonText="Read our full returns policy"
          buttonProps={{
            href: "https://www.johnlewis.com/customer-services/returns?dt=informationOnOurReturnsPolicy",
            rel: "noopener noreferrer",
            tag: "a",
          }}
        />
      );

    return (
      <ReturnsExclusions
        browsePageHierarchy={browsePageHierarchy}
        sellingProductType={sellingProductType}
      />
    );
  };

  return (
    <div className={styles.returns} data-testid="delivery:returns">
      <>
        <div className={styles.buttons}>
          <SecondaryButton
            tag="a"
            href="https://www.johnlewis.com/customer-services/delivery-information"
            labelText="More about delivery and collection"
            width="hug"
          />
        </div>
        {showChristmasDeliveryMessaging && (
          <>
            <h3
              className={styles.subTitle__xs}
              data-testid="delivery:inTimeForChristmas"
            >
              Get it in time for Christmas
            </h3>
            <p className={styles.noMargin}>
              <a href="https://www.johnlewis.com/customer-services/delivery-information/christmas-delivery-schedules">
                See our last order dates for Christmas
              </a>{" "}
              (Subject to change depending on demand)
            </p>
          </>
        )}

        <h2 className={styles.subTitle} data-testid="delivery:returns:title">
          Returns and refunds
        </h2>

        <ReturnsRestrictionsMessage />

        <div className={styles.returnsCopy}>
          {showOutletReturnsMessage && (
            <p>Outlet items have the same returns policy as brand new stock.</p>
          )}
          {isMeteredProduct || isM2M ? null : showTimedFrameCopy ? (
            <p>
              You can return{" "}
              {!isBladedArticle ? "or exchange an unwanted" : "an unwanted"}{" "}
              item within 30 days of receiving it. During the festive season, we
              have an extended returns period. Items purchased between 26
              September and 24 December can be returned{" "}
              {!isBladedArticle ? "or exchanged" : ""} until 23 January 2025.
            </p>
          ) : (
            <p>
              You can return {!isBladedArticle ? "or exchange " : ""}an unwanted
              item within 30 days of receiving it.
            </p>
          )}

          <p>
            Please allow up to 14 days from sending the item to receive your
            refund.
          </p>

          {!isBladedArticle ? (
            <>
              <h3 className={styles.subTitle__sm}>How to return your item</h3>
              <h3 className={styles.subTitle__xs}>Small items</h3>
              <p>
                Return a small item you bought online for free to a John Lewis
                or Waitrose shop, or{" "}
                <a
                  href="https://www.johnlewis.com/customer-services/returns/how-to-return-your-item#return-locations"
                  rel="noreferrer"
                >
                  one of our return locations
                </a>
                . You can also arrange a collection using Evri or Royal Mail.
              </p>
              <h3 className={styles.subTitle__xs}>
                Large items - items delivered by two people, such as appliances
                and furniture
              </h3>
              <p className={styles.homeCollection}>
                We&apos;ll collect large items from you. Collections are free if
                your item is damaged or faulty. If not, we charge a collection
                fee. To arrange a collection,{" "}
                <a
                  href="https://www.johnlewis.com/customer-services/contact-us?dt=returnsAndRefunds"
                  rel="noreferrer"
                >
                  please contact us
                </a>
                .
              </p>
            </>
          ) : null}
        </div>

        <div className={styles.buttons}>
          <SecondaryButton
            tag="a"
            href="https://www.johnlewis.com/customer-services/returns?dt=informationOnOurReturnsPolicy"
            labelText="Read our returns policy"
            width="hug"
          />
          <SecondaryButton
            tag="a"
            href="https://www.johnlewis.com/customer-services/returns"
            labelText="More about returns and refunds"
            width="hug"
          />
        </div>
      </>
    </div>
  );
};
